import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, X, Lock, Crown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel, Pill } from "../components/Primitives";
import { cn } from "../lib/utils";

export default function Lessons() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    api.get("/lessons").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((l) => l.category).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((l) => {
      const matchesCat = category === "All" || l.category === category;
      const matchesQ =
        !q ||
        l.title?.toLowerCase().includes(q) ||
        l.subtitle?.toLowerCase().includes(q) ||
        l.category?.toLowerCase().includes(q);
      return matchesCat && matchesQ;
    });
  }, [items, query, category]);

  return (
    <div data-testid="lessons-page">
      <div className="border-b border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12">
          <SectionLabel>For Educators</SectionLabel>
          <h1 className="font-serif text-5xl font-semibold text-bone lg:text-6xl">Lessons & Articles</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted">
            College-level lessons structured to teach un-erased history with academic rigor and honest complexity.
          </p>

          {!user?.is_premium && (
            <div className="mt-6 flex flex-wrap items-center gap-3 border border-gold/50 bg-gold/5 px-4 py-3" data-testid="lessons-premium-banner">
              <Crown className="h-4 w-4 shrink-0 text-gold" />
              <p className="text-sm text-bone">
                Your first lesson is <span className="text-gold">free to preview</span> — unlock the full library for $3/month.
              </p>
              <Link to="/premium" data-testid="lessons-banner-premium-link" className="text-sm text-gold underline-offset-4 hover:underline">
                Go Premium
              </Link>
            </div>
          )}

          {/* Search */}
          <div className="relative mt-8 max-w-xl">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lessons — e.g. Songhai, colonialism, Timbuktu…"
              data-testid="lessons-search-input"
              className="w-full border border-edge bg-ink py-3 pl-11 pr-10 text-bone placeholder:text-muted focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="lessons-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-gold">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Category filter */}
          <div className="mt-4 flex flex-wrap gap-2" data-testid="category-filter">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={cn(
                  "border px-4 py-1.5 text-sm transition-colors",
                  category === c ? "border-gold bg-gold text-ink" : "border-edge text-muted hover:border-gold/50 hover:text-gold"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-12">
          <p className="mb-8 font-mono text-[11px] uppercase tracking-[0.15em] text-muted" data-testid="lessons-count">
            {filtered.length} {filtered.length === 1 ? "lesson" : "lessons"}
            {category !== "All" ? ` in ${category}` : ""}{query ? ` matching “${query}”` : ""}
          </p>

          {filtered.length === 0 ? (
            <div className="border border-dashed border-edge p-16 text-center" data-testid="lessons-empty">
              <p className="text-muted">No lessons match your search.</p>
              <button onClick={() => { setQuery(""); setCategory("All"); }} className="mt-3 text-sm text-gold hover:underline">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-3">
              {filtered.map((l, i) => {
                const locked = !user?.is_premium && !l.free_preview;
                return (
                <motion.div key={l.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 6) * 0.06 }}>
                  <Link
                    to={`/lessons/${l.slug}`}
                    data-testid={locked ? `lesson-locked-${l.slug}` : `lesson-card-${l.slug}`}
                    className="group flex h-full flex-col overflow-hidden border border-edge bg-ink transition-colors hover:border-gold/50"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img src={l.image_url} alt={l.title} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                      <div className="absolute left-4 top-4"><Pill tone="gold">{l.category}</Pill></div>
                      {!user?.is_premium && (
                        <div className="absolute right-4 top-4">
                          {l.free_preview ? (
                            <span className="border border-gold bg-ink/80 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-gold" data-testid="lesson-free-preview-badge">
                              Free Preview
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 border border-edge bg-ink/80 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                              <Lock className="h-3 w-3" /> Premium
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-serif text-2xl text-bone group-hover:text-gold">{l.title}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted">{l.subtitle}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted">{l.reading_level}</span>
                        {locked ? (
                          <Lock className="h-4 w-4 text-muted transition-colors group-hover:text-gold" />
                        ) : (
                          <ArrowRight className="h-4 w-4 text-gold transition-transform group-hover:translate-x-1" />
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
